import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface DeploymentRow {
  readonly deploymentId: string;
  readonly applicationId: string;
  readonly versionId: string;
  readonly environment: string;
  readonly deploymentStatus: string;
  readonly requestedBy: string | null;
  readonly approvedBy: string | null;
  /** Named by the server (G18) — the screen never shows the uuids the row stores. */
  readonly applicationCode: string | null;
  readonly applicationName: string | null;
  readonly versionNo: number | null;
  readonly versionLabel: string | null;
  readonly requestedByName: string | null;
  readonly requestedByCode: string | null;
  readonly approvedByName: string | null;
  readonly approvedByCode: string | null;
}

function applicationOf(d: DeploymentRow): string {
  if (!d.applicationCode) return '—';
  return d.applicationName ? `${d.applicationCode} — ${d.applicationName}` : d.applicationCode;
}

function versionOf(d: DeploymentRow): string {
  return d.versionLabel ?? (d.versionNo === null ? '—' : `v${d.versionNo}`);
}

function deploymentOf(d: DeploymentRow): string {
  return `${d.applicationCode ?? '—'} ${versionOf(d)} · ${d.environment}`;
}

const ENVIRONMENTS = ['', 'SANDBOX', 'TESTING', 'UAT', 'PRODUCTION'];
const DEPLOY_STATUSES = ['', 'PENDING', 'APPROVED', 'DEPLOYED', 'FAILED', 'ROLLED_BACK'];
const REQUEST_ENVIRONMENTS = ENVIRONMENTS.filter(Boolean);

/**
 * M28 — Deployment Dashboard (§20 / §32). Tracks application promotions across
 * the SANDBOX → TESTING → UAT → PRODUCTION pipeline. Lists every
 * application_deployment (T375) with its target environment, lifecycle status,
 * requester and approver — enforcing BR-02 (no direct-to-production without an
 * independent approval) and BR-07 (rollback always available). Supports
 * request → approve → execute, plus fail and rollback, each wired to the
 * guarded /v1/lcnc/deployments write API. Filterable by environment and status.
 */
export function M28DeploymentDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<DeploymentRow[]>([]);
  const [environment, setEnvironment] = useState('');
  const [deploymentStatus, setDeploymentStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [targetEnv, setTargetEnv] = useState('SANDBOX');
  const [failReason, setFailReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (environment) params.set('environment', environment);
    if (deploymentStatus) params.set('deploymentStatus', deploymentStatus);
    const qs = params.toString();
    apiRequest<{ data: DeploymentRow[] }>(`/v1/lcnc/deployments${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, environment, deploymentStatus, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m28.deployment.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (fn: () => Promise<unknown>, successMsg: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const request = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim() || !versionId.trim()) { setActionError(t('m28.deployment.validationIds')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/lcnc/deployments`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), versionId: versionId.trim(), environment: targetEnv },
        token,
      });
      setNotice(t('m28.deployment.noticeRequested', { env: targetEnv }));
      setApplicationId('');
      setVersionId('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, t('m28.deployment.verbRequest'), 'DEPLOY_SANDBOX'));
    } finally {
      setBusy(false);
    }
  };

  const approve = (d: DeploymentRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/deployments/${d.deploymentId}/approve`, { method: 'POST', body: {}, token }),
      t('m28.deployment.noticeApproved', { id: deploymentOf(d) }), t('m28.deployment.verbApprove'), 'DEPLOY_APPROVE',
    );

  const execute = (d: DeploymentRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/deployments/${d.deploymentId}/execute`, { method: 'POST', body: {}, token }),
      t('m28.deployment.noticeExecuted', { id: deploymentOf(d) }), t('m28.deployment.verbExecute'), 'DEPLOY_PRODUCTION',
    );

  const fail = (d: DeploymentRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/deployments/${d.deploymentId}/fail`, {
        method: 'POST', body: { reason: failReason.trim() || 'Unspecified' }, token,
      }),
      t('m28.deployment.noticeFailed', { id: deploymentOf(d) }), t('m28.deployment.verbExecute'), 'DEPLOY_PRODUCTION',
    );

  const rollback = (d: DeploymentRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/deployments/${d.deploymentId}/rollback`, { method: 'POST', body: {}, token }),
      t('m28.deployment.noticeRolledBack', { id: deploymentOf(d) }), t('m28.deployment.verbRollback'), 'ROLLBACK',
    );

  return (
    <main aria-label="deployment-dashboard">
      <h1>{t('m28.deployment.title')}</h1>

      <section aria-label="request-deployment">
        <h2>{t('m28.deployment.requestHeading')}</h2>
        <form aria-label="deployment-form" onSubmit={(e) => void request(e)}>
          <LcncAppPicker label={t('m28.deployment.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <RecordPicker<{ versionId: string; versionNo?: number; versionLabel?: string }>
            endpoint={applicationId ? `/v1/lcnc/applications/${applicationId}/versions` : ''}
            idField="versionId"
            label={t('m28.deployment.fieldVersionId')}
            testId="field-version-id"
            value={versionId}
            optionLabel={(v) => v.versionLabel ?? `v${v.versionNo ?? '?'}`}
            onPick={setVersionId}
            emptyText={applicationId ? 'No versions yet' : 'Select an application first'}
          />
          <label>{t('m28.deployment.fieldEnvironment')}
            <select aria-label="field-environment" value={targetEnv} onChange={(e) => setTargetEnv(e.target.value)}>
              {REQUEST_ENVIRONMENTS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-deployment" disabled={busy}>{busy ? t('m28.deployment.requesting') : t('m28.deployment.requestButton')}</button>
        </form>
        <label>{t('m28.deployment.fieldFailReason')}<input aria-label="field-fail-reason" value={failReason} onChange={(e) => setFailReason(e.target.value)} /></label>
        {actionError && <p role="alert" aria-label="deployment-error">{actionError}</p>}
        {notice && <p aria-label="deployment-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m28.deployment.filterEnvironment')}
          <select aria-label="filter-environment" value={environment} onChange={(e) => setEnvironment(e.target.value)}>
            {ENVIRONMENTS.map((v) => <option key={v || 'all'} value={v}>{v || t('m28.deployment.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m28.deployment.filterStatus')}
          <select aria-label="filter-deployment-status" value={deploymentStatus} onChange={(e) => setDeploymentStatus(e.target.value)}>
            {DEPLOY_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m28.deployment.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m28.deployment.loading')}</p> : (
        <>
          <p aria-label="deployment-count">{t('m28.deployment.count', { count: rows.length })}</p>
          <table aria-label="deployment-table">
            <thead><tr><th>{t('m28.deployment.colApplication')}</th><th>{t('m28.deployment.colVersion')}</th><th>{t('m28.deployment.colEnvironment')}</th><th>{t('m28.deployment.colStatus')}</th><th>{t('m28.deployment.colRequestedBy')}</th><th>{t('m28.deployment.colApprovedBy')}</th><th>{t('m28.deployment.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.deploymentId}>
                  <td>{applicationOf(d)}</td><td>{versionOf(d)}</td><td>{d.environment}</td>
                  <td>{d.deploymentStatus}</td><td>{personLabel(d.requestedByName, d.requestedByCode) ?? '—'}</td><td>{personLabel(d.approvedByName, d.approvedByCode) ?? '—'}</td>
                  <td>
                    <button type="button" aria-label={`approve-${d.deploymentId}`} onClick={() => void approve(d)}>{t('m28.deployment.btnApprove')}</button>
                    <button type="button" aria-label={`execute-${d.deploymentId}`} onClick={() => void execute(d)}>{t('m28.deployment.btnExecute')}</button>
                    <button type="button" aria-label={`fail-${d.deploymentId}`} onClick={() => void fail(d)}>{t('m28.deployment.btnFail')}</button>
                    <button type="button" aria-label={`rollback-${d.deploymentId}`} onClick={() => void rollback(d)}>{t('m28.deployment.btnRollback')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
